// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — Flow Schema: Node/Edge factories and constants
// ═══════════════════════════════════════════════════════════════

import { MarkerType } from '@xyflow/react';
import type {
  TipoNodoFlujograma,
  FlowNode,
  FlowEdge,
  FlowNodeData,
} from '../../types/flowchart.types';

// Re-export node components for React Flow registration
import { ActivityNode } from '../nodes/ActivityNode';
import { DecisionNode } from '../nodes/DecisionNode';
import { DocumentNode } from '../nodes/DocumentNode';
import { SubprocessNode } from '../nodes/SubprocessNode';
import { StartNode } from '../nodes/StartNode';
import { EndNode } from '../nodes/EndNode';
import { ProcessRefNode } from '../nodes/ProcessRefNode';
import { GovernanceNode } from '../nodes/GovernanceNode';
import { ResultNode } from '../nodes/ResultNode';
import { OrthogonalEdge } from '../edges/OrthogonalEdge';

// ─── Default Node Sizes ─────────────────────────────────────────────────────

export const NODE_SIZES: Record<TipoNodoFlujograma, { w: number; h: number }> = {
  ACTIVIDAD: { w: 90, h: 38 },
  DECISION: { w: 38, h: 38 },
  DOCUMENTO: { w: 90, h: 38 },
  SUBPROCESO: { w: 90, h: 38 },
  INICIO: { w: 90, h: 38 },
  FIN: { w: 90, h: 38 },
  PROCESO_REF: { w: 38, h: 38 },
  PROCESO_GOBIERNO: { w: 90, h: 38 },
  RESULTADO: { w: 90, h: 38 },
};

// ─── React Flow Node Type Registration Map ──────────────────────────────────

export const NODE_TYPES = {
  ACTIVIDAD: ActivityNode,
  DECISION: DecisionNode,
  DOCUMENTO: DocumentNode,
  SUBPROCESO: SubprocessNode,
  INICIO: StartNode,
  FIN: EndNode,
  PROCESO_REF: ProcessRefNode,
  PROCESO_GOBIERNO: GovernanceNode,
  RESULTADO: ResultNode,
} as const;

// ─── React Flow Edge Type Registration Map ──────────────────────────────────

export const EDGE_TYPES = {
  orthogonal: OrthogonalEdge,
} as const;

// ─── Default Labels ─────────────────────────────────────────────────────────

export const NODE_TYPE_LABELS: Record<TipoNodoFlujograma, string> = {
  ACTIVIDAD: 'Actividad',
  DECISION: 'Decision',
  DOCUMENTO: 'Documento',
  SUBPROCESO: 'Subproceso',
  INICIO: 'Inicio',
  FIN: 'Fin',
  PROCESO_REF: 'Proceso Ref.',
  PROCESO_GOBIERNO: 'Proc. Gobierno',
  RESULTADO: 'Resultado',
};

// ─── Node Factory ───────────────────────────────────────────────────────────

/**
 * Create a new FlowNode with a unique ID and default data.
 */
export function createNode(
  type: TipoNodoFlujograma,
  position: { x: number; y: number },
  data?: Partial<FlowNodeData>,
): FlowNode {
  const id = crypto.randomUUID();
  const size = NODE_SIZES[type];

  const baseData: FlowNodeData = {
    label: data?.label ?? NODE_TYPE_LABELS[type],
    nodeType: type,
    ...data,
  } as FlowNodeData;

  return {
    id,
    type,
    position,
    data: baseData,
    width: size.w,
    height: size.h,
  };
}

// ─── Edge Factory ───────────────────────────────────────────────────────────

/**
 * Create a new FlowEdge connecting two nodes.
 */
export function createEdge(
  source: string,
  target: string,
  label?: string,
  sourceHandle?: string,
): FlowEdge {
  const id = crypto.randomUUID();

  return {
    id,
    source,
    target,
    type: 'orthogonal',
    label: label ?? undefined,
    sourceHandle: sourceHandle ?? undefined,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#2F5496', strokeWidth: 1.5 },
  };
}
